import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, assetUrl } from '../api/index.js';
import { useAuth } from '../contexts/AuthContext.jsx';

function HeartIcon({ filled }) {
  return (
    <svg
      className={`w-6 h-6 ${filled ? 'text-red-500 fill-red-500' : 'text-gray-400'}`}
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={filled ? 0 : 2}
        d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
    </svg>
  );
}

export default function PostDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liking, setLiking] = useState(false);

  useEffect(() => {
    api.posts.get(id)
      .then(setPost)
      .catch(() => navigate('/feed'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  async function handleLike() {
    if (!user) { navigate('/signin'); return; }
    if (liking) return;
    setLiking(true);
    const wasLiked = post.liked;
    setPost((p) => ({ ...p, liked: !p.liked, like_count: p.like_count + (wasLiked ? -1 : 1) }));
    try {
      await api.posts.like(id);
    } catch {
      setPost((p) => ({ ...p, liked: wasLiked, like_count: p.like_count + (wasLiked ? 1 : -1) }));
    } finally {
      setLiking(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm('Delete this post?')) return;
    await api.posts.delete(id);
    navigate('/feed');
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-4 border-brand-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!post) return null;

  const isOwner = user?.sub === post.user_id || user?.id === post.user_id;

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 mb-4">
        ← Back
      </button>

      <div className="card overflow-hidden">
        {/* Image */}
        <div className="bg-black">
          <img src={assetUrl(post.image_url)} alt={post.caption || 'Post'} className="w-full max-h-[70vh] object-contain" />
        </div>

        {/* Info */}
        <div className="p-5">
          {/* Author */}
          <div className="flex items-center gap-3 mb-4">
            {post.avatar_url ? (
              <img src={assetUrl(post.avatar_url)} alt={post.display_name} className="w-10 h-10 rounded-full object-cover" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-brand-100 flex items-center justify-center text-brand-700 font-bold">
                {post.display_name?.[0]?.toUpperCase()}
              </div>
            )}
            <div>
              <button
                onClick={() => navigate(`/caddies/${post.user_id}`)}
                className="font-semibold text-gray-900 hover:text-brand-700"
              >
                {post.display_name}
              </button>
              <p className="text-xs text-gray-400">
                {new Date(post.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>

          {/* Caption */}
          {post.caption && <p className="text-gray-700 mb-4">{post.caption}</p>}

          {/* Actions */}
          <div className="flex items-center gap-4 border-t border-gray-100 pt-4">
            <button
              onClick={handleLike}
              className="flex items-center gap-2 text-sm hover:opacity-80 transition-opacity"
            >
              <HeartIcon filled={!!post.liked} />
              <span className={post.liked ? 'text-red-500 font-medium' : 'text-gray-500'}>
                {post.like_count} {post.like_count === 1 ? 'like' : 'likes'}
              </span>
            </button>

            {user && !isOwner && (
              <button
                onClick={() => navigate(`/messages/${post.user_id}`)}
                className="btn-primary text-sm"
              >
                💬 Send DM
              </button>
            )}

            {isOwner && (
              <button onClick={handleDelete} className="ml-auto text-sm text-red-500 hover:text-red-700">
                Delete post
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
